import { GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import {
    CalibrationFilesMap,
    CheckDetails,
    DeviceWithSticks,
    OnboardDeviceConnectionStatus,
    OnboardDevicesField,
    OnboardDevicesFilterOperator,
    OnboardDevicesFilters,
    OnboardDevicesQueryParams,
    OnboardDevicesSortBy,
    VersionValidity,
} from '../interfaces';

export const SCANNER_INPUT_RESET_TIMEOUT_MS = 100;

export const ONBOARD_DEVICE_SERVER_SORT_FIELDS = new Set<string>([
    'deviceId',
    'deviceSerial',
    'matrixCode',
    'status',
    'baseImageS3Link',
    'commitHash',
    'zymkeySerial',
    'rpiSerial',
    'username',
    'hostname',
    'sshPort',
    'accessPointSSID',
    'vpnProfile',
    'vpnIPAddress',
    'vpnPubkey',
    'bundleDevicePubkey',
    'bundleCloudPubkey',
    'mapS3Link',
    'zymkeyPubkey',
    'deviceSSHPubkey',
    'deviceSSHPubkeyType',
    'deviceSSHPubkeyHash',
    'systemVersion',
    'hardwareConfiguration',
    'vpnOnlineAt',
    'createdAt',
    'updatedAt',
]);

export const ONBOARD_DEVICE_SERVER_FILTER_FIELD_MAP: Record<string, OnboardDevicesField> = {
    deviceId: 'deviceId',
    deviceSerial: 'deviceSerial',
    matrixCode: 'matrixCode',
    status: 'status',
    baseImageS3Link: 'baseImageS3Link',
    commitHash: 'commitHash',
    zymkeySerial: 'zymkeySerial',
    rpiSerial: 'rpiSerial',
    username: 'username',
    hostname: 'hostname',
    sshPort: 'sshPort',
    accessPointSSID: 'accessPointSSID',
    vpnProfile: 'vpnProfile',
    vpnIPAddress: 'vpnIPAddress',
    vpnPubkey: 'vpnPubkey',
    bundleDevicePubkey: 'bundleDevicePubkey',
    bundleCloudPubkey: 'bundleCloudPubkey',
    mapS3Link: 'mapS3Link',
    zymkeyPubkey: 'zymkeyPubkey',
    deviceSSHPubkey: 'deviceSSHPubkey',
    deviceSSHPubkeyType: 'deviceSSHPubkeyType',
    deviceSSHPubkeyHash: 'deviceSSHPubkeyHash',
    systemVersion: 'systemVersion',
    hardwareConfiguration: 'hardwareConfiguration',
    vpnOnlineAt: 'vpnOnlineAt',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
    organization: 'organizationId',
};

export const ONBOARD_DEVICE_NUMERIC_FILTER_FIELDS = new Set<string>(['deviceSerial', 'sshPort']);
const SERVER_FILTER_OPERATORS = new Set([
    'contains',
    'equals',
    'notEquals',
    '=',
    '!=',
    'isAnyOf',
    'isNull',
    'isNotNull',
]);
const NO_VALUE_FILTER_OPERATORS = new Set(['isNull', 'isNotNull']);

export const isOnboardDeviceServerSortable = (field: string): boolean => {
    return ONBOARD_DEVICE_SERVER_SORT_FIELDS.has(field);
};

export const isOnboardDeviceServerFilterable = (field: string): boolean => {
    return Object.prototype.hasOwnProperty.call(ONBOARD_DEVICE_SERVER_FILTER_FIELD_MAP, field);
};

export const getOnboardDevicesSortQueryPatch = (
    model: GridSortModel,
): Pick<OnboardDevicesQueryParams, 'sortBy' | 'sortOrder'> => {
    const sortItem = model.find((item) => isOnboardDeviceServerSortable(item.field) && item.sort);

    if (!sortItem) {
        return {
            sortBy: undefined,
            sortOrder: undefined,
        };
    }

    return {
        sortBy: sortItem.field as OnboardDevicesSortBy,
        sortOrder: sortItem.sort === 'asc' ? 'asc' : 'desc',
    };
};

export const buildOnboardDevicesFiltersFromGridFilterModel = (
    model?: GridFilterModel,
): OnboardDevicesFilters => {
    const filters: OnboardDevicesFilters = [];

    model?.items?.forEach((item) => {
        const field = ONBOARD_DEVICE_SERVER_FILTER_FIELD_MAP[item.field];

        if (!field) {
            return;
        }

        const operator = (item.operator || 'equals') as OnboardDevicesFilterOperator;

        if (!SERVER_FILTER_OPERATORS.has(operator)) {
            return;
        }

        if (NO_VALUE_FILTER_OPERATORS.has(operator)) {
            filters.push({ field, operator });
            return;
        }

        if (item.value === undefined || item.value === null || item.value === '') {
            return;
        }

        const isNumericField = ONBOARD_DEVICE_NUMERIC_FILTER_FIELDS.has(field);

        if (operator === 'isAnyOf') {
            const filterValues = (Array.isArray(item.value) ? item.value : [item.value])
                .map((value) => {
                    if (value === undefined || value === null || value === '') {
                        return null;
                    }

                    if (isNumericField) {
                        const numericValue = Number(value);
                        return Number.isFinite(numericValue) ? numericValue : null;
                    }

                    const stringValue = String(value).trim();
                    return stringValue ? stringValue : null;
                })
                .filter((value): value is string | number => value !== null);

            if (filterValues.length > 0) {
                filters.push({
                    field,
                    operator,
                    value: filterValues,
                });
            }

            return;
        }

        if (Array.isArray(item.value)) {
            return;
        }

        const value = typeof item.value === 'string' ? item.value.trim() : item.value;

        if (value === '') {
            return;
        }

        if (isNumericField) {
            const numericValue = Number(value);
            if (Number.isFinite(numericValue)) {
                filters.push({ field, operator, value: numericValue });
            }

            return;
        }

        filters.push({ field, operator, value: String(value) });
    });

    return filters;
};

export const withScannedCodesFilter = (
    filters: OnboardDevicesFilters,
    scannedCodes: string[],
): OnboardDevicesFilters => {
    if (scannedCodes.length === 0) {
        return filters;
    }

    return [...filters, { field: 'matrixCode', operator: 'isAnyOf', value: scannedCodes }];
};

export const getSelectedRowsForDevices = (selectedRows: string[], devices: DeviceWithSticks[]): string[] => {
    const visibleIds = new Set(
        devices.map((device) => device.deviceId).filter((deviceId): deviceId is string => Boolean(deviceId)),
    );

    return selectedRows.filter((rowId) => visibleIds.has(rowId));
};

export const mergeChecksAndCalibrationFiles = (
    checksDetails?: CheckDetails[],
    calibrationFiles?: CalibrationFilesMap,
): Record<string, unknown> => {
    const result: Record<string, unknown> = {};

    Object.entries(calibrationFiles ?? {}).forEach(([key, file]) => {
        if (file) {
            result[key] = file;
        }
    });

    (checksDetails ?? []).forEach((check) => {
        const key = check?.Key;
        if (!key) {
            return;
        }

        if (!Array.isArray(result[key])) {
            result[key] = [];
        }

        (result[key] as CheckDetails[]).push(check);
    });

    return result;
};

export const isVersionTheSame = (
    collection: DeviceWithSticks['activationSticks'],
    parentDeviceVersion: string,
) => {
    return collection.every((item) => item.systemVersion === parentDeviceVersion);
};

export const buildOnboardDeviceRows = (
    devices: DeviceWithSticks[],
    hardwareConfigurationNameById: Map<string, string>,
    onlineStatusByDeviceId?: Record<string, OnboardDeviceConnectionStatus>,
) => {
    return devices.map((device) => {
        const { checksDetails, calibrationFiles, ...deviceRest } = device;

        const row: any = {
            ...deviceRest,
            activationSticksCount: device.activationSticks.length,
            status: device.status || 'Unknown',
            id: device.deviceId,
            onlineConnectionStatus: device.deviceId ? onlineStatusByDeviceId?.[device.deviceId] : undefined,
        };

        row.organization = device.organizationDetails?.name || 'None';
        row.hardwareConfiguration =
            hardwareConfigurationNameById.get(device.hardwareConfiguration ?? '') ?? '';
        row.checks = mergeChecksAndCalibrationFiles(checksDetails, calibrationFiles);
        row.upToDate =
            device.systemVersion && isVersionTheSame(device.activationSticks, device.systemVersion)
                ? VersionValidity.VALID
                : VersionValidity.NOT_VALID;

        if (row.updatedAt) row.updatedAt = new Date(row.updatedAt);
        if (row.createdAt) row.createdAt = new Date(row.createdAt);

        return row;
    });
};
